/**
 * Multi-object tracking over COCO “person” boxes (IOU matching, missed-frame pruning, concurrent cap).
 * Semantics of exported counts: `packages/shared/models/audience-heartbeat-aggregate-semantics.md`.
 */

/** Normalized bbox: x,y top-left; w,h width/height in 0–1 (fraction of frame). */
export interface ICvPersonBBox {
  x: number
  y: number
  w: number
  h: number
}

export function iouNormalized(a: ICvPersonBBox, b: ICvPersonBBox): number {
  const ax2 = a.x + a.w
  const ay2 = a.y + a.h
  const bx2 = b.x + b.w
  const by2 = b.y + b.h
  const ix1 = Math.max(a.x, b.x)
  const iy1 = Math.max(a.y, b.y)
  const ix2 = Math.min(ax2, bx2)
  const iy2 = Math.min(ay2, by2)
  const iw = Math.max(0, ix2 - ix1)
  const ih = Math.max(0, iy2 - iy1)
  const inter = iw * ih
  const ua = a.w * a.h + b.w * b.h - inter
  if (ua <= 0) {
    return 0
  }
  return inter / ua
}

interface IInternalTrack {
  bbox: ICvPersonBBox
  firstMs: number
  lastMs: number
  missedFrames: number
  hitCount: number
}

export interface IAudienceCvPersonWindowMetrics {
  cvPersonModelId: string
  /** Qualified tracks that started in this window (not unique visitors). */
  personQualifiedTrackCount: number
  /** Sum of qualified-track dwell (seconds), each capped by `intervalSeconds` (non-dedup). */
  personDwellSeconds: number
}

/**
 * Keeps the `max` strongest tracks: higher `hitCount`, then more recent `lastMs`, then newer `firstMs`.
 * Mitigates pathological detector output (memory / CPU bound).
 */
function pruneTracksToMax(tracks: IInternalTrack[], max: number): IInternalTrack[] {
  if (tracks.length <= max) {
    return tracks
  }
  return [...tracks].sort((a, b) => {
    if (b.hitCount !== a.hitCount) {
      return b.hitCount - a.hitCount
    }
    if (b.lastMs !== a.lastMs) {
      return b.lastMs - a.lastMs
    }
    return b.firstMs - a.firstMs
  }).slice(0, max)
}

/**
 * IOU matching, missed-frame pruning, optional concurrent-track cap.
 * `endWindow` counts tracks whose **first** detection lies in [windowStartMs, windowEndMs] and that meet min span or min hits.
 */
export function createAudienceCvPersonTracker(opts: {
  /** Same string as heartbeat `cvPersonModelId` / config `modelId`. */
  cvPersonModelId: string
  iouMatchThreshold: number
  maxMissedFrames: number
  minTrackSpanMs: number
  minHitCount: number
  /** Upper bound on simultaneous tracks after each `processFrame` (inclusive). */
  maxConcurrentTracks: number
}) {
  let tracks: IInternalTrack[] = []

  function processFrame(boxes: ICvPersonBBox[], nowMs: number): void {
    const used = new Set<number>()
    for (const t of tracks) {
      let bestIdx = -1
      let bestIou = 0
      for (let i = 0; i < boxes.length; i++) {
        if (used.has(i)) {
          continue
        }
        const iou = iouNormalized(t.bbox, boxes[i]!)
        if (iou > bestIou && iou >= opts.iouMatchThreshold) {
          bestIou = iou
          bestIdx = i
        }
      }
      if (bestIdx >= 0) {
        t.bbox = boxes[bestIdx]!
        t.lastMs = nowMs
        t.missedFrames = 0
        t.hitCount += 1
        used.add(bestIdx)
      }
      else {
        t.missedFrames += 1
      }
    }
    tracks = tracks.filter(t => t.missedFrames <= opts.maxMissedFrames)
    for (let i = 0; i < boxes.length; i++) {
      if (used.has(i)) {
        continue
      }
      const b = boxes[i]!
      tracks.push({
        bbox: b,
        firstMs: nowMs,
        lastMs: nowMs,
        missedFrames: 0,
        hitCount: 1
      })
    }
    tracks = pruneTracksToMax(tracks, opts.maxConcurrentTracks)
  }

  function endWindow(windowStartMs: number, windowEndMs: number, intervalSeconds: number): IAudienceCvPersonWindowMetrics {
    let personQualifiedTrackCount = 0
    let personDwellSeconds = 0
    for (const t of tracks) {
      if (t.firstMs < windowStartMs || t.firstMs > windowEndMs) {
        continue
      }
      const spanMs = t.lastMs - t.firstMs
      if (spanMs < opts.minTrackSpanMs && t.hitCount < opts.minHitCount) {
        continue
      }
      personQualifiedTrackCount += 1
      personDwellSeconds += Math.min(intervalSeconds, spanMs / 1000)
    }
    tracks = []
    return {
      cvPersonModelId: opts.cvPersonModelId,
      personQualifiedTrackCount,
      personDwellSeconds: Math.round(personDwellSeconds * 1000) / 1000
    }
  }

  function reset(): void {
    tracks = []
  }

  return { processFrame, endWindow, reset }
}
